import {Component, computed, effect, inject, signal, Signal, untracked} from '@angular/core';
import {Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';
import {ColDef} from 'ag-grid-community';
import {firstValueFrom, take} from 'rxjs';
import {IStore} from '../../../../shared/shared-models/store/store';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {
  SharedModalFiltersAdvancedContainerComponent,
} from '../../../../shared/shared-components/components/shared-advanced-filters/shared-modal-filters-advanced-container/shared-modal-filters-advanced-container.component';
import {ISharedModalFiltersAdvanced} from '../../../../shared/shared-models/modals/shared-modal-filters-advanced';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {
  selectAdvancedFilterGroups,
  selectIsAdvancedFilterGroupsLoading,
} from '../../../core/store-shared-filter/shared-filter.selectors';
import {
  copyGeneralFiltersToStockTake,
  copyGeneralFiltersToStockTakeFailure,
  copyGeneralFiltersToStockTakeSuccess,
  getAdvancedFilterGroups,
  setAdvancedFilterGroupGrid,
} from '../../../core/store-shared-filter/shared-filter.actions';
import {IAdvancedFilterGroup} from '../../models/general/advanced-filter-group.interface';
import {
  ADVANCED_FILTER_GRID_GENERAL,
  ADVANCED_FILTER_GRID_STOCK_TAKE,
  AdvancedFilterGridCategory,
  matchesAdvancedFilterGridCategory,
} from '../../models/general/advanced-filter-grid.constants';
import {stockManagerGridColDefs} from '../../../stock-manager/models/stock-manager-grid-col-def';
import {buildAdvancedFilterCopyResultLines} from '../../utils/advanced-filter-firestore.utils';

/**
 * Settings page for managing saved advanced filter groups.
 *
 * Renders two cards using the shared widget-container layout:
 *  - "Copy to Stock Take Filters" — copy General filters into Stock Take on this store.
 *  - "Advanced Filters" — category switch plus an Edit button that opens the
 *    shared advanced-filters editor for the selected category.
 */
@Component({
  selector: 'app-feature-advanced-filters',
  templateUrl: './feature-advanced-filters.component.html',
  styleUrl: './feature-advanced-filters.component.scss',
  standalone: false,
})
export class FeatureAdvancedFiltersComponent {

  //#region INJECTORS
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _modalUtils = inject(SharedModalUtils);
  private readonly _modalController = inject(ModalControllerService);
  //#endregion

  //#region COL DEFS — match the Stock Manager grid the modal targets
  protected readonly colDefs: ColDef[] = [...stockManagerGridColDefs];
  protected readonly colDefsIgnore: string[] = ['disabled', 'icons', 'nominalGP'];
  //#endregion

  //#region SIGNALS / ICONS
  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;

  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _allFilters: Signal<IAdvancedFilterGroup[]> = this._store.selectSignal(selectAdvancedFilterGroups);
  private readonly _isAdvancedFilterGroupsLoading: Signal<boolean> = this._store.selectSignal(
    selectIsAdvancedFilterGroupsLoading,
  );

  protected readonly selectedCategory = signal<AdvancedFilterGridCategory>(ADVANCED_FILTER_GRID_GENERAL);
  protected readonly selectedGeneralToStockTakeIds = signal<Set<number>>(new Set<number>());
  protected readonly isCopyingToStockTake = signal<boolean>(false);

  protected readonly categoryFilters = computed<IAdvancedFilterGroup[]>(() => {
    const category = this.selectedCategory();
    return this._allFilters().filter((filter: IAdvancedFilterGroup): boolean =>
      matchesAdvancedFilterGridCategory(filter.grid, category),
    );
  });

  protected readonly categoryOptions = computed<Record<string, ISelectOption>>(() => ({
    [ADVANCED_FILTER_GRID_GENERAL]: {
      name: 'General Filters',
      disabled: false,
      selected: this.selectedCategory() === ADVANCED_FILTER_GRID_GENERAL,
    },
    [ADVANCED_FILTER_GRID_STOCK_TAKE]: {
      name: 'Stock Take Filters',
      disabled: false,
      selected: this.selectedCategory() === ADVANCED_FILTER_GRID_STOCK_TAKE,
    },
  }));

  protected readonly generalFilterOptions = computed<Record<string, ISelectOption>>(() => {
    const filters = this._allFilters().filter(
      (f: IAdvancedFilterGroup): boolean =>
        f.saved && matchesAdvancedFilterGridCategory(f.grid, ADVANCED_FILTER_GRID_GENERAL),
    );
    const selected = this.selectedGeneralToStockTakeIds();
    const opts: Record<string, ISelectOption> = {};
    filters.forEach((f: IAdvancedFilterGroup): void => {
      const key = String(f.id);
      opts[key] = {
        name: f.name?.trim() || `Filter ${f.id}`,
        selected: selected.has(f.id),
        disabled: false,
      };
    });
    return opts;
  });

  protected readonly categoryLabel = computed<string>(() =>
    this.selectedCategory() === ADVANCED_FILTER_GRID_STOCK_TAKE
      ? 'Stock Take Filters'
      : 'General Filters',
  );

  protected readonly editCardDescription = computed<string>(() =>
    this.selectedCategory() === ADVANCED_FILTER_GRID_STOCK_TAKE
      ? 'Filter groups created here are available when creating a stock take or schedule.'
      : 'Filter groups created here are available in the Stock Manager grid.',
  );
  //#endregion

  //#region LIFECYCLE
  constructor() {
    effect(() => {
      const storeId = this._selectedStore()?.storeId;
      if (!storeId) return;
      untracked(() => {
        this.selectedGeneralToStockTakeIds.set(new Set<number>());
        this.selectedCategory.set(ADVANCED_FILTER_GRID_GENERAL);
        this._store.dispatch(setAdvancedFilterGroupGrid({grid: ADVANCED_FILTER_GRID_GENERAL}));
        this._store.dispatch(getAdvancedFilterGroups());
      });
    });

    effect(() => {
      const category = this.selectedCategory();
      untracked(() => {
        this._store.dispatch(setAdvancedFilterGroupGrid({grid: category}));
      });
    });

    effect(() => {
      const filters = this._allFilters();
      const selected = this.selectedGeneralToStockTakeIds();
      if (selected.size === 0) {
        return;
      }
      const generalIds = new Set<number>(
        filters
          .filter(
            (f: IAdvancedFilterGroup): boolean =>
              f.saved && matchesAdvancedFilterGridCategory(f.grid, ADVANCED_FILTER_GRID_GENERAL),
          )
          .map((f: IAdvancedFilterGroup): number => f.id),
      );
      const pruned = new Set<number>([...selected].filter((id: number): boolean => generalIds.has(id)));
      if (pruned.size !== selected.size) {
        untracked(() => {
          this.selectedGeneralToStockTakeIds.set(pruned);
        });
      }
    });
  }
  //#endregion

  //#region ACCESSORS
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get hasGeneralFilters(): boolean {
    return Object.keys(this.generalFilterOptions()).length > 0;
  }

  get canCopyToStockTake(): boolean {
    return (
      this.selectedGeneralToStockTakeIds().size > 0
      && !this._isAdvancedFilterGroupsLoading()
      && !this.isCopyingToStockTake()
    );
  }
  //#endregion

  //#region HANDLERS
  protected onCategoryChange(selection: Record<string, ISelectOption>): void {
    const key = Object.keys(selection).find((k: string) => selection[k].selected);
    if (key === ADVANCED_FILTER_GRID_GENERAL || key === ADVANCED_FILTER_GRID_STOCK_TAKE) {
      if (this.selectedCategory() === key) {
        return;
      }
      this.selectedCategory.set(key);
    }
  }

  /**
   * Open the shared advanced-filters editor as a modal for the selected category.
   */
  protected async onEditFiltersClick(): Promise<void> {
    const category = this.selectedCategory();
    const modalTitle = category === ADVANCED_FILTER_GRID_STOCK_TAKE
      ? 'Stock Take Filters'
      : 'General Filters';
    const componentProps: ISharedModalFiltersAdvanced = {
      buttonCloseCross: true,
      colDefs: this.colDefs,
      colDefsIgnore: this.colDefsIgnore,
      gridReference: category,
      modalTitle,
    };
    const modal = await this._modalController.create({
      component: SharedModalFiltersAdvancedContainerComponent,
      componentProps,
      cssClass: ['shared-modal-filters-advanced'],
    });
    await modal.present();
    await modal.onDidDismiss();
    this.selectedGeneralToStockTakeIds.set(new Set<number>());
    this._store.dispatch(getAdvancedFilterGroups());
  }

  protected onGeneralToStockTakeSelectionChange(selection: Record<string, ISelectOption>): void {
    const ids = new Set<number>();
    Object.entries(selection).forEach(([key, opt]: [string, ISelectOption]): void => {
      if (opt.selected) {
        const id = Number(key);
        if (!Number.isNaN(id)) ids.add(id);
      }
    });
    this.selectedGeneralToStockTakeIds.set(ids);
  }

  protected async onCopyToStockTakeClick(): Promise<void> {
    if (this.isCopyingToStockTake()) return;
    const store = this._selectedStore();
    const filterIds = Array.from(this.selectedGeneralToStockTakeIds());
    if (!store?.storeId || filterIds.length === 0) return;

    this.isCopyingToStockTake.set(true);
    const completion$ = this._actions$.pipe(
      ofType(copyGeneralFiltersToStockTakeSuccess, copyGeneralFiltersToStockTakeFailure),
      take(1),
    );
    this._store.dispatch(copyGeneralFiltersToStockTake({store, filterIds}));

    let finalAction:
      | ReturnType<typeof copyGeneralFiltersToStockTakeSuccess>
      | ReturnType<typeof copyGeneralFiltersToStockTakeFailure>;
    try {
      finalAction = await firstValueFrom(completion$);
    } finally {
      this.isCopyingToStockTake.set(false);
    }

    if (finalAction.type === copyGeneralFiltersToStockTakeSuccess.type) {
      const {copiedCount, skippedCount, failures} = finalAction;
      const lines: string[] = buildAdvancedFilterCopyResultLines(
        {copiedCount, skippedCount, failures},
        {
          copiedLine: (count: number): string => {
            const noun = count === 1 ? 'filter' : 'filters';
            return `${count} general ${noun} copied to Stock Take Filters for "${store.name}".`;
          },
          skippedLine: (count: number): string => {
            const noun = count === 1 ? 'filter was' : 'filters were';
            return `${count} selected general ${noun} already available as Stock Take Filters and were not copied again.`;
          },
          failureHeader: (count: number): string => {
            const noun = count === 1 ? 'filter' : 'filters';
            return `${count} general ${noun} could not be copied:`;
          },
        },
      );
      if (lines.length === 0) {
        lines.push('No filters were copied. Select saved General Filters to copy.');
      }
      const failedCount: number = failures.length;
      const allFailed = failedCount > 0 && copiedCount === 0;
      const partialFailure = failedCount > 0 && copiedCount > 0;
      await this._modalUtils.createModal({
        modalTitleIcon: allFailed ? IconsEnum.alertBox : IconsEnum.checkCircleOutline,
        modalTitleIconColor: allFailed ? IconColourEnum.red : partialFailure ? IconColourEnum.yellow : IconColourEnum.green,
        modalTitle: allFailed
          ? 'Copy Failed'
          : partialFailure
            ? 'Filters Partially Copied'
            : copiedCount > 0
              ? 'Filters Copied'
              : 'Nothing to Copy',
        contextDescription: lines,
        buttonAcceptText: '',
        buttonCloseText: 'OK',
        buttonAccept: false,
        buttonClose: true,
        buttonCloseCross: false,
        cssClass: ['shared-basic-modal-css-small'],
      });
      if (copiedCount > 0) {
        this.selectedGeneralToStockTakeIds.set(new Set<number>());
      }
    } else {
      const message: string = finalAction.error?.message?.trim();
      await this._modalUtils.createModal({
        modalTitleIcon: IconsEnum.alertBox,
        modalTitleIconColor: IconColourEnum.red,
        modalTitle: 'Copy Failed',
        contextDescription: [
          message
            ? `Failed to copy filters to Stock Take Filters: ${message}`
            : 'Failed to copy filters to Stock Take Filters. Please try again.',
        ],
        buttonAcceptText: '',
        buttonCloseText: 'OK',
        buttonAccept: false,
        buttonClose: true,
        buttonCloseCross: false,
        cssClass: ['shared-basic-modal-css-small'],
      });
    }
  }
  //#endregion
}
